import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useState } from 'react'

const PROMO_NAMESPACE = 'loadline:promo:ios-launch-2026-03'
const HAS_SEEN_MODAL_KEY = `${PROMO_NAMESPACE}:seen-modal`

const readStoredFlag = (key: string): boolean => {
  if (typeof window === 'undefined') return false

  try {
    return window.localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

const writeStoredFlag = (key: string, value: boolean): void => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(key, value ? 'true' : 'false')
  } catch {
    // Ignore storage failures. The promo should fail open, not crash the app shell.
  }
}

const addPromoBreadcrumb = (message: string, data?: Record<string, string | boolean>) => {
  Sentry.addBreadcrumb({
    category: 'loadline.promo',
    message,
    level: 'info',
    data
  })
}

export function useLoadlineIOSPromo() {
  const [isModalOpen, setIsModalOpen] = useState(() => !readStoredFlag(HAS_SEEN_MODAL_KEY))

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== HAS_SEEN_MODAL_KEY) {
        return
      }

      if (readStoredFlag(HAS_SEEN_MODAL_KEY)) {
        setIsModalOpen(false)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const markModalSeen = useCallback(() => {
    writeStoredFlag(HAS_SEEN_MODAL_KEY, true)
    addPromoBreadcrumb('ios_launch_modal_seen')
  }, [])

  const setModalOpen = useCallback((open: boolean) => {
    setIsModalOpen(open)
    addPromoBreadcrumb(open ? 'ios_launch_modal_opened' : 'ios_launch_modal_closed')
  }, [])

  const openModal = useCallback(() => {
    setIsModalOpen(true)
    addPromoBreadcrumb('ios_launch_modal_reopened_from_sidebar')
  }, [])

  return {
    isModalOpen,
    isSidebarVisible: true,
    setModalOpen,
    markModalSeen,
    openModal
  }
}
